import type { TFunction } from "i18next";

export interface PaywallCopyItem {
  title: string;
  description: string;
}

export interface PaywallCopy {
  title: string;
  subtitle?: string;
  useAltImage: boolean;
  items: PaywallCopyItem[];
  // Static labels (kept here so callers don't need to know t-keys)
  freeTrial: string;
  monthly: string;
  annual: string;
  topLabel: string;
  nextTime: string;
  loading: string;
  loadingPrice: string;
  heroAlt: string;
}

interface PaywallCopyOptions {
  variant?: "default" | "ai";
  isNecTrialEligible?: boolean;
  partialLevelAccess?: boolean;
}

/**
 * Returns localized paywall copy for the given app + variant.
 * Mirrors the previous in-component switch on APP_TO_BUILD/variant.
 * Pricing (numeric amounts) is intentionally kept out of this helper —
 * Phase F formats prices via Intl.NumberFormat at the call site.
 */
export function getPaywallCopy(
  t: TFunction,
  appToBuild: string,
  options: PaywallCopyOptions = {},
): PaywallCopy {
  const {
    variant = "default",
    isNecTrialEligible = false,
    partialLevelAccess = false,
  } = options;

  const base = {
    freeTrial: t("paywall.free_trial"),
    monthly: t("paywall.monthly"),
    annual: t("paywall.annual"),
    nextTime: t("paywall.next_time"),
    loading: t("paywall.loading"),
    loadingPrice: t("paywall.loading_price"),
    heroAlt: t("paywall.hero_alt"),
  };

  if (variant === "ai") {
    return {
      ...base,
      title: t("paywall.title_ai"),
      subtitle: t("paywall.subtitle_ai"),
      useAltImage: true,
      topLabel: t("paywall.label_ai_tutor"),
      items: [
        {
          title: t("paywall.ai_item1_title"),
          description: t("paywall.ai_item1_description"),
        },
        {
          title: t("paywall.ai_item2_title"),
          description: t("paywall.ai_item2_description"),
        },
        {
          title: t("paywall.ai_item3_title"),
          description: t("paywall.ai_item3_description"),
        },
      ],
    };
  }

  switch (appToBuild) {
    case "electrical":
      if (partialLevelAccess) {
        return {
          ...base,
          title: t("paywall.title_electrical_upgrade"),
          subtitle: t("paywall.subtitle_electrical_upgrade"),
          useAltImage: false,
          topLabel: t("paywall.label_all_access"),
          items: [
            {
              title: t("upgrade.all_access_feature_levels_title"),
              description: t("upgrade.all_access_feature_levels_body"),
            },
            {
              title: t("upgrade.all_access_feature_red_seal_title"),
              description: t("upgrade.all_access_feature_red_seal_body"),
            },
            {
              title: t("upgrade.all_access_feature_questions_title"),
              description: t("upgrade.all_access_feature_questions_body"),
            },
            {
              title: t("upgrade.all_access_feature_codebook_title"),
              description: t("upgrade.all_access_feature_codebook_body"),
            },
          ],
        };
      }
      return {
        ...base,
        title: t("paywall.title_electrical_default"),
        useAltImage: false,
        topLabel: t("paywall.label_all_access"),
        items: [
          {
            title: t("paywall.electrical_item1_title"),
            description: t("paywall.electrical_item1_description"),
          },
          {
            title: t("paywall.electrical_item2_title"),
            description: t("paywall.electrical_item2_description"),
          },
          {
            title: t("paywall.electrical_item3_title"),
            description: t("paywall.electrical_item3_description"),
          },
          {
            title: t("paywall.electrical_item4_title"),
            description: t("paywall.electrical_item4_description"),
          },
        ],
      };
    case "electrical_us":
      return {
        ...base,
        title: isNecTrialEligible
          ? t("paywall.title_electrical_us_trial")
          : t("paywall.title_electrical_us_default"),
        subtitle: isNecTrialEligible
          ? t("paywall.subtitle_electrical_us_trial")
          : undefined,
        useAltImage: false,
        topLabel: t("paywall.label_pro"),
        items: [
          {
            title: t("paywall.electrical_us_item1_title"),
            description: t("paywall.electrical_us_item1_description"),
          },
          {
            title: t("paywall.electrical_us_item2_title"),
            description: t("paywall.electrical_us_item2_description"),
          },
          {
            title: t("paywall.electrical_us_item3_title"),
            description: t("paywall.electrical_us_item3_description"),
          },
          {
            title: t("paywall.electrical_us_item4_title"),
            description: t("paywall.electrical_us_item4_description"),
          },
        ],
      };
    case "plumbing":
      if (partialLevelAccess) {
        return {
          ...base,
          title: t("paywall.title_plumbing_upgrade"),
          subtitle: t("paywall.subtitle_plumbing_upgrade"),
          useAltImage: false,
          topLabel: t("paywall.label_all_access"),
          items: [
            {
              title: t("paywall.plumbing_upgrade_item1_title"),
              description: t("paywall.plumbing_upgrade_item1_description"),
            },
            {
              title: t("paywall.plumbing_upgrade_item2_title"),
              description: t("paywall.plumbing_upgrade_item2_description"),
            },
            {
              title: t("paywall.plumbing_upgrade_item3_title"),
              description: t("paywall.plumbing_upgrade_item3_description"),
            },
            {
              title: t("paywall.plumbing_upgrade_item4_title"),
              description: t("paywall.plumbing_upgrade_item4_description"),
            },
          ],
        };
      }
      return {
        ...base,
        title: t("paywall.title_plumbing_default"),
        useAltImage: false,
        topLabel: t("paywall.label_pro"),
        items: [
          {
            title: t("paywall.plumbing_total_coverage_title"),
            description: t("paywall.plumbing_total_coverage_subtitle"),
          },
          {
            title: t("paywall.plumbing_item1_title"),
            description: t("paywall.plumbing_item1_description"),
          },
          {
            title: t("paywall.plumbing_item2_title"),
            description: t("paywall.plumbing_item2_description"),
          },
          {
            title: t("paywall.plumbing_item3_title"),
            description: t("paywall.plumbing_item3_description"),
          },
          {
            title: t("paywall.plumbing_item4_title"),
            description: t("paywall.plumbing_item4_description"),
          },
          {
            title: t("paywall.plumbing_item5_title"),
            description: t("paywall.plumbing_item5_description"),
          },
        ],
      };
    case "ibew_aptitude":
      return {
        ...base,
        title: t("paywall.title_ibew_aptitude_default"),
        subtitle: t("paywall.subtitle_ibew_aptitude_default"),
        useAltImage: true,
        topLabel: t("paywall.label_pro"),
        items: [
          {
            title: t("paywall.ibew_item1_title"),
            description: t("paywall.ibew_item1_description"),
          },
          {
            title: t("paywall.ibew_item2_title"),
            description: t("paywall.ibew_item2_description"),
          },
          {
            title: t("paywall.ibew_item3_title"),
            description: t("paywall.ibew_item3_description"),
          },
          {
            title: t("paywall.ibew_item4_title"),
            description: t("paywall.ibew_item4_description"),
          },
        ],
      };
    default:
      return {
        ...base,
        title: "",
        useAltImage: false,
        topLabel: t("paywall.label_pro"),
        items: [],
      };
  }
}
